type ClientErrorPayload = {
  type: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  extra?: Record<string, unknown>;
};

let lastSignature = '';
let lastSentAt = 0;

const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  return isLocal ? 'http://localhost:5000' : '';
};

const shouldThrottle = (signature: string): boolean => {
  const now = Date.now();
  if (signature === lastSignature && now - lastSentAt < 5000) {
    return true;
  }
  lastSignature = signature;
  lastSentAt = now;
  return false;
};

const sendClientError = (payload: ClientErrorPayload) => {
  const apiBase = getApiBaseUrl();
  if (!apiBase && typeof window !== 'undefined') {
    // Relative path fallback for ngrok/proxy deployments
    payload.url = payload.url || window.location.href;
  }

  const signature = `${payload.type}:${payload.message}:${payload.filename || ''}:${payload.lineno || ''}:${payload.colno || ''}`;
  if (shouldThrottle(signature)) return;

  const body = JSON.stringify({
    ...payload,
    url: payload.url || (typeof window !== 'undefined' ? window.location.href : ''),
    userAgent: payload.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    timestamp: payload.timestamp || new Date().toISOString(),
  });

  const endpoint = `${apiBase}/api/client-error`;
  try {
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch (_) {
    // Ignore network/reporting errors
  }
};

export const reportClientError = (payload: ClientErrorPayload) => {
  sendClientError(payload);
};

export const initClientErrorReporter = () => {
  if (typeof window === 'undefined') return;

  const onError = (message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) => {
    const isStringMessage = typeof message === 'string';
    const errorMessage = isStringMessage ? message : 'Unknown error';

    sendClientError({
      type: 'error',
      message: errorMessage,
      stack: error?.stack,
      filename: source,
      lineno,
      colno,
    });
    return false;
  };

  window.onerror = onError;

  window.addEventListener('error', (e) => {
    const target = e.target as Element | null;
    const targetTag = target?.tagName;
    const targetSrc = (target as any)?.src || (target as any)?.href;

    sendClientError({
      type: 'error',
      message: e.message || 'Unknown error',
      stack: e.error?.stack,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      extra: e.target
        ? {
            targetTag,
            targetSrc,
          }
        : undefined,
    });
  }, true);

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    let message = 'Unhandled rejection';
    let stack: string | undefined;

    if (reason instanceof Error) {
      message = reason.message || message;
      stack = reason.stack;
    } else if (typeof reason === 'string') {
      message = reason;
    } else if (reason && typeof reason === 'object') {
      message = JSON.stringify(reason);
    }

    sendClientError({
      type: 'unhandledrejection',
      message,
      stack,
    });
  });

  // Mirror console.error to backend for additional context
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    try {
      const message = args.map((arg) => {
        if (arg instanceof Error) return arg.message;
        if (typeof arg === 'string') return arg;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }).join(' ');
      sendClientError({
        type: 'error',
        message: message || 'console.error',
        extra: { console: 'error', args: args.map(String) },
      });
    } catch (_) {
      // ignore reporter failures
    }
    originalError(...args);
  };
};
